import { useTracker } from '../../analytics/Analytics';
import { trackProductEvent } from '../../analytics/tracking';
import { useCallback, useContext } from 'react';
import get from 'lodash.get';
import { CartIdContext } from '../Cart/CartIdContext';
import ProductVariationContext from '../ProductVariationProvider/ProductVariationContext';
import { getAddToCartOptimisticResponse } from './cartMutationUtils';
import useWarningInDev from '../../hooks/useWarningInDev';
import uniqueId from '../../helpers/uniqueId';
import { addToCartSuccess, addToCartError, getArticleNumber } from './AddToCart/addToCartUtils';
// At the moment this is not portable because it has to receive the mutation as
// an argument. When we can use `useMutation`, this will become a lot nicer and
// remove the need for a wrapping Container component
// Ultimately, the API will be
// const { addToCart, missingOption } = useCartAdder({onAddToCart})
function useCartAdder({ mutate, client, onAddToCart, onAddToCartInit, onAddToCartError, onAddToCartSuccess, cartQuery }) {
    const { selectedVariation, setMissingOption, missingOption, hasMissingOption } = useContext(ProductVariationContext);
    const track = useTracker();
    const { cartId, setCartId } = useContext(CartIdContext);
    const trackProductVariant = useCallback(function trackProductVariant(product) {
        track(trackProductEvent({
            action: 'add',
            product,
            selectedVariation
        }));
    }, [selectedVariation, track]);
    function addToCart(product, quantity, comments) {
        const articleNumber = getArticleNumber(product, selectedVariation);
        // This checks if any of the article numbers of variants are not unique.
        // This should theoretically not be allowed in the admin, but currently
        // nothing prevents it, so we need to check here
        if (get(product, 'variants.options', []).length > 0) {
            const articleNos = product.variants.values.map(val => val.articleNumber);
            if (articleNos.includes(product.articleNumber)) {
                console.error(`Product ${product.articleNumber} has variants with non-unique articleNumbers`);
                return;
            }
        }
        // Return early if the article number is invalid
        if (!articleNumber) {
            setMissingOption();
            return;
        }
        // Track the product with analytics
        trackProductVariant(product);
        const mutationId = uniqueId();
        const callbackOptions = {
            product,
            quantity,
            selectedVariation,
            mutationId
        };
        mutate({
            variables: {
                input: {
                    cartId,
                    articleNumber,
                    quantity,
                    comments: comments || []
                }
            },
            optimisticResponse: {
                addToCart: {
                    cart: getAddToCartOptimisticResponse(product, quantity, cartId, client, cartQuery),
                    __typename: 'CartMutation'
                }
            }
        })
            .then(addToCartSuccess({
            onAddToCartSuccess,
            callbackOptions,
            cartId,
            setCartId
        }))
            .catch(addToCartError({ onAddToCartError, callbackOptions }));
        // Call the callback
        onAddToCartInit && onAddToCartInit(callbackOptions);
        onAddToCart && onAddToCart(selectedVariation);
    }
    useWarningInDev(!!onAddToCart, '`AddToCart`/`useCartAdder`: `onAddToCart`-prop has been been deprecated in favour of `onAddToCartInit`.');
    return { addToCart, missingOption, hasMissingOption };
}
export default useCartAdder;
//# sourceMappingURL=useCartAdder.js.map